import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  alpha,
  useTheme,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Fade,
  Chip,
  Slide,
} from "@mui/material";
import {
  Receipt,
  ChevronLeft,
  ChevronRight,
  Phone,
  Clear,
  TableRestaurant,
  TakeoutDining,
  FilterList,
  ViewModule,
  ViewList,
} from "@mui/icons-material";
import DashboardOrderCard from "./DashboardOrderCard";
import OrdersListTable from "./OrdersListTable";
import POSKeyboard from "../POSKeyboard";

// Modern Loading Component - Overlay style to prevent layout shifts
const LoadingOverlay = () => {
  const theme = useTheme();

  return (
    <Fade in={true} timeout={300}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha("#ffffff", 0.8),
          backdropFilter: "blur(2px)",
          borderRadius: "12px",
          zIndex: 2,
        }}
      >
        {/* Animated Background Circle */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 50,
              height: 50,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${alpha("#667eea", 0.15)}, ${alpha("#764ba2", 0.08)})`,
              animation: "pulse 2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%": {
                  transform: "scale(1)",
                  opacity: 0.7,
                },
                "50%": {
                  transform: "scale(1.15)",
                  opacity: 0.4,
                },
                "100%": {
                  transform: "scale(1)",
                  opacity: 0.7,
                },
              },
            }}
          />
          <CircularProgress
            size={28}
            thickness={3.5}
            sx={{
              color: "#667eea",
              zIndex: 1,
            }}
          />
        </Box>

        {/* Loading Text */}
        <Typography
          variant="caption"
          sx={{
            mt: 1.5,
            color: theme.palette.text.secondary,
            fontWeight: 500,
            fontSize: "0.75rem",
            animation: "fadeInOut 1.5s ease-in-out infinite",
            "@keyframes fadeInOut": {
              "0%": { opacity: 0.6 },
              "50%": { opacity: 1 },
              "100%": { opacity: 0.6 },
            },
          }}
        >
          Loading orders...
        </Typography>
      </Box>
    </Fade>
  );
};

const DashboardOrdersSection = ({
  ordersData,
  loading = false,
  onViewAll,
  onSelectOrder,
  onPageChange,
  viewMode = "grid",
  onViewModeChange,
  customerMobileSearch,
  setCustomerMobileSearch,
  orderTypeFilter,
  setOrderTypeFilter,
}) => {
  const theme = useTheme();
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [activeKb, setActiveKb] = useState(null);

  // Extract data from API response
  const orders = ordersData?.data || [];
  const totalOrders = ordersData?.totalItems || 0; // Use actual filtered data length
  const totalPages = ordersData?.totalPages || 0;
  const currentPage = ordersData?.currentPage || 0;
  const ordersPerPage = ordersData?.pageSize || 10;

  // Pagination calculations
  const calculatePaginationDisplay = () => {
    if (totalOrders === 0) return { start: 0, end: 0, total: 0 };

    const start = currentPage * ordersPerPage + 1;
    const end = Math.min((currentPage + 1) * ordersPerPage, totalOrders);

    return { start, end, total: totalOrders };
  };

  // Navigation handlers
  const navigationHandlers = {
    changePage: (newPage) => {
      if (newPage >= 0 && newPage < totalPages && onPageChange) {
        onPageChange(newPage);
      }
    },
    goToFirstPage: () => navigationHandlers.changePage(0),
    goToLastPage: () => navigationHandlers.changePage(totalPages - 1),
    goToPreviousPage: () => navigationHandlers.changePage(currentPage - 1),
    goToNextPage: () => navigationHandlers.changePage(currentPage + 1),
  };

  // Swipe functionality
  const swipeHandlers = {
    minDistance: 50,
    onTouchStart: (e) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    },
    onTouchMove: (e) => {
      setTouchEnd(e.targetTouches[0].clientX);
    },
    onTouchEnd: () => {
      if (!touchStart || !touchEnd) return;

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > swipeHandlers.minDistance;
      const isRightSwipe = distance < -swipeHandlers.minDistance;

      if (isLeftSwipe && currentPage < totalPages - 1) {
        navigationHandlers.goToNextPage();
      }
      if (isRightSwipe && currentPage > 0) {
        navigationHandlers.goToPreviousPage();
      }
    },
  };

  // Component state checks
  const hasOrders = orders.length > 0;
  const hasMultiplePages = totalPages > 1;
  const paginationDisplay = calculatePaginationDisplay();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "14px",
        width: "100%",
        bgcolor: "white",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "100%",
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.08),
        boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          borderColor: alpha("#667eea", 0.2),
          boxShadow: `0 8px 24px ${alpha("#667eea", 0.08)}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1.75,
          gap: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${alpha("#667eea", 0.15)}, ${alpha("#764ba2", 0.05)})`,
            }}
          >
            <Receipt sx={{ fontSize: 18, color: "#667eea" }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                letterSpacing: "-0.5px",
                fontSize: "1rem",
              }}
            >
              Ongoing Orders
            </Typography>
            {(paginationDisplay.total > 0 || loading) && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress
                      size={12}
                      thickness={4}
                      sx={{ color: "#667eea" }}
                    />
                    <span>Loading...</span>
                  </Box>
                ) : (
                  <>
                    {paginationDisplay.start}-{paginationDisplay.end} of{" "}
                    {totalOrders}
                  </>
                )}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Filters and Search */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {/* Order Type Filter Chips */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <FilterList
              sx={{
                fontSize: 17,
                color: theme.palette.text.secondary,
              }}
            />
            <Chip
              label="All"
              onClick={() => setOrderTypeFilter && setOrderTypeFilter("all")}
              sx={{
                height: 34,
                minWidth: 64,
                fontWeight: 600,
                fontSize: "0.78rem",
                px: 1,
                bgcolor:
                  orderTypeFilter === "all"
                    ? alpha("#667eea", 0.15)
                    : alpha(theme.palette.grey[300], 0.3),
                color:
                  orderTypeFilter === "all"
                    ? "#667eea"
                    : theme.palette.text.secondary,
                border: `2px solid ${orderTypeFilter === "all"
                  ? alpha("#667eea", 0.4)
                  : alpha(theme.palette.divider, 0.2)
                  }`,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor:
                    orderTypeFilter === "all"
                      ? alpha("#667eea", 0.25)
                      : alpha(theme.palette.grey[300], 0.5),
                  transform: "scale(1.05)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
            <Chip
              icon={
                <TableRestaurant
                  sx={{
                    fontSize: "20px !important",
                    color:
                      orderTypeFilter === "dine-in"
                        ? "#10b981 !important"
                        : `${theme.palette.text.secondary} !important`,
                  }}
                />
              }
              label="Dine-in"
              onClick={() =>
                setOrderTypeFilter && setOrderTypeFilter("dine-in")
              }
              sx={{
                height: 34,
                minWidth: 90,
                fontWeight: 600,
                fontSize: "0.78rem",
                px: 1,
                bgcolor:
                  orderTypeFilter === "dine-in"
                    ? alpha("#10b981", 0.15)
                    : alpha(theme.palette.grey[300], 0.3),
                color:
                  orderTypeFilter === "dine-in"
                    ? "#10b981"
                    : theme.palette.text.secondary,
                border: `2px solid ${orderTypeFilter === "dine-in"
                  ? alpha("#10b981", 0.4)
                  : alpha(theme.palette.divider, 0.2)
                  }`,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor:
                    orderTypeFilter === "dine-in"
                      ? alpha("#10b981", 0.25)
                      : alpha(theme.palette.grey[300], 0.5),
                  transform: "scale(1.05)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                "& .MuiChip-label": {
                  px: 1,
                },
                "& .MuiChip-icon": {
                  ml: 0.5,
                },
              }}
            />
            <Chip
              icon={
                <TakeoutDining
                  sx={{
                    fontSize: "20px !important",
                    color:
                      orderTypeFilter === "takeaway"
                        ? "#f59e0b !important"
                        : `${theme.palette.text.secondary} !important`,
                  }}
                />
              }
              label="Takeaway"
              onClick={() =>
                setOrderTypeFilter && setOrderTypeFilter("takeaway")
              }
              sx={{
                height: 34,
                minWidth: 102,
                fontWeight: 600,
                fontSize: "0.78rem",
                px: 1,
                bgcolor:
                  orderTypeFilter === "takeaway"
                    ? alpha("#f59e0b", 0.15)
                    : alpha(theme.palette.grey[300], 0.3),
                color:
                  orderTypeFilter === "takeaway"
                    ? "#f59e0b"
                    : theme.palette.text.secondary,
                border: `2px solid ${orderTypeFilter === "takeaway"
                  ? alpha("#f59e0b", 0.4)
                  : alpha(theme.palette.divider, 0.2)
                  }`,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor:
                    orderTypeFilter === "takeaway"
                      ? alpha("#f59e0b", 0.25)
                      : alpha(theme.palette.grey[300], 0.5),
                  transform: "scale(1.05)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                "& .MuiChip-label": {
                  px: 1,
                },
                "& .MuiChip-icon": {
                  ml: 0.5,
                },
              }}
            />
          </Box>

          {/* Customer Mobile Search */}
          <TextField
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="Search by mobile..."
            value={customerMobileSearch || ""}
            onChange={(e) =>
              setCustomerMobileSearch && setCustomerMobileSearch(e.target.value)
            }
            onFocus={() => setActiveKb("mobile")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {loading && customerMobileSearch ? (
                    <CircularProgress
                      size={18}
                      thickness={4}
                      sx={{ color: "#10b981" }}
                    />
                  ) : (
                    <Phone
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 20,
                      }}
                    />
                  )}
                </InputAdornment>
              ),
              endAdornment: customerMobileSearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setCustomerMobileSearch && setCustomerMobileSearch("")
                    }
                    sx={{ p: 0.5 }}
                  >
                    <Clear
                      sx={{
                        fontSize: 18,
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: 180,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: alpha("#10b981", 0.04),
                height: 36,
                fontSize: "0.8rem",
                border: "2px solid",
                borderColor: alpha(theme.palette.divider, 0.08),
                "&:hover": {
                  bgcolor: alpha("#10b981", 0.06),
                  borderColor: alpha("#10b981", 0.2),
                },
                "&.Mui-focused": {
                  bgcolor: "white",
                  borderColor: "#10b981",
                },
              },
            }}
          />

          {/* View Toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.35,
              p: 0.35,
              borderRadius: "10px",
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              bgcolor: alpha(theme.palette.grey[100], 0.6),
            }}
          >
            <IconButton
              size="small"
              onClick={() => onViewModeChange && onViewModeChange("grid")}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                bgcolor:
                  viewMode === "grid"
                    ? alpha("#667eea", 0.16)
                    : "transparent",
                color:
                  viewMode === "grid"
                    ? "#667eea"
                    : theme.palette.text.secondary,
                "&:hover": {
                  bgcolor:
                    viewMode === "grid"
                      ? alpha("#667eea", 0.2)
                      : alpha(theme.palette.grey[300], 0.45),
                },
              }}
            >
              <ViewModule sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onViewModeChange && onViewModeChange("list")}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                bgcolor:
                  viewMode === "list"
                    ? alpha("#667eea", 0.16)
                    : "transparent",
                color:
                  viewMode === "list"
                    ? "#667eea"
                    : theme.palette.text.secondary,
                "&:hover": {
                  bgcolor:
                    viewMode === "list"
                      ? alpha("#667eea", 0.2)
                      : alpha(theme.palette.grey[300], 0.45),
                },
              }}
            >
              <ViewList sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {!hasOrders && !loading ? (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Receipt sx={{ fontSize: 48, color: "#D1D5DB", mb: 2, mx: "auto" }} />
          <Typography
            variant="body1"
            sx={{ color: "#6B7280", fontWeight: 600 }}
          >
            No ongoing orders
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Loading Overlay */}
          {loading && <LoadingOverlay />}

          {/* Content Area */}
          <Box
            onTouchStart={swipeHandlers.onTouchStart}
            onTouchMove={swipeHandlers.onTouchMove}
            onTouchEnd={swipeHandlers.onTouchEnd}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              pr: 0.5,
              position: "relative",
              touchAction: "pan-y",
              opacity: loading ? 0.3 : 1,
              transition: "opacity 0.2s ease",
              minHeight: 0,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha("#667eea", 0.3),
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: alpha("#667eea", 0.5),
                },
              },
            }}
          >
            {viewMode === "list" ? (
              <OrdersListTable
                orders={orders}
                onSelectOrder={onSelectOrder}
              />
            ) : (
              <Grid container spacing={1.25} sx={{ alignItems: "flex-start" }}>
                {orders.map((order) => (
                  <Grid
                    size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                    key={order.id}
                    sx={{ minWidth: 0 }}
                  >
                    <DashboardOrderCard
                      order={order}
                      viewMode={viewMode}
                      enforceFixedHeight={false}
                      onClick={() => onSelectOrder && onSelectOrder(order)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      )}

      {/* Swipe Navigation - Clean Mobile-First Design */}
      {hasMultiplePages && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          {/* Left Navigation */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={navigationHandlers.goToPreviousPage}
              disabled={currentPage === 0}
              size="small"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor:
                  currentPage === 0
                    ? alpha("#000", 0.05)
                    : alpha("#667eea", 0.1),
                color: currentPage === 0 ? alpha("#000", 0.3) : "#667eea",
                border: `1px solid ${currentPage === 0 ? alpha("#000", 0.1) : alpha("#667eea", 0.2)}`,
                "&:hover": {
                  bgcolor:
                    currentPage === 0
                      ? alpha("#000", 0.05)
                      : alpha("#667eea", 0.15),
                  transform: currentPage === 0 ? "none" : "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <ChevronLeft sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.75rem",
                display: { xs: "none", sm: "block" },
              }}
            >
              Swipe to navigate
            </Typography>
          </Box>

          {/* Page Indicator Dots */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {Array.from({ length: totalPages }, (_, index) => (
              <Box
                key={index}
                onClick={() => navigationHandlers.changePage(index)}
                sx={{
                  width: index === currentPage ? 24 : 8,
                  height: 8,
                  borderRadius: "4px",
                  bgcolor:
                    index === currentPage ? "#667eea" : alpha("#667eea", 0.2),
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor:
                      index === currentPage ? "#5a67d8" : alpha("#667eea", 0.4),
                    transform: "scale(1.1)",
                  },
                }}
              />
            ))}
          </Box>

          {/* Right Navigation */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.75rem",
                display: { xs: "none", sm: "block" },
              }}
            >
              Page {currentPage + 1} of {totalPages}
            </Typography>
            <IconButton
              onClick={navigationHandlers.goToNextPage}
              disabled={currentPage === totalPages - 1}
              size="small"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor:
                  currentPage === totalPages - 1
                    ? alpha("#000", 0.05)
                    : alpha("#667eea", 0.1),
                color:
                  currentPage === totalPages - 1
                    ? alpha("#000", 0.3)
                    : "#667eea",
                border: `1px solid ${currentPage === totalPages - 1 ? alpha("#000", 0.1) : alpha("#667eea", 0.2)}`,
                "&:hover": {
                  bgcolor:
                    currentPage === totalPages - 1
                      ? alpha("#000", 0.05)
                      : alpha("#667eea", 0.15),
                  transform:
                    currentPage === totalPages - 1 ? "none" : "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <ChevronRight sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* POSKeyboard */}
      <Slide
        direction="up"
        in={activeKb === "mobile"}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1400,
            display: "flex",
            justifyContent: "center",
            bgcolor: alpha("#000", 0.3),
            pb: 2,
            pt: 1,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 920 }}>
            <POSKeyboard
              value={customerMobileSearch || ""}
              onChange={(val) =>
                setCustomerMobileSearch && setCustomerMobileSearch(val)
              }
              onClose={() => setActiveKb(null)}
              onEnter={() => setActiveKb(null)}
              numericOnly={true}
            />
          </Box>
        </Box>
      </Slide>
    </Paper>
  );
};

export default DashboardOrdersSection;
